import {groupBy} from 'ramda';

import {Nullish} from 'shared/web';

import {Option} from '../types/VehicleCatalogueLogic';
import {
  CustomTenantMakeModelData,
  GroupType,
  VehicleMakeModelData,
  VehicleModelDescriptor,
  VehicleTypeEnumObject,
} from '../types/VehicleMakeModelParsers';

const DEFAULT_MODEL_GROUP = 'MODEL_DEFAULT';

interface VehicleModelParserProps {
  vehicleType: VehicleTypeEnumObject | Nullish;
  customMakeLabel: string | Nullish;
  fallbackMakeLabel: string;
  unknownModels?: Option[];
  vehicleModelData: VehicleMakeModelData | Nullish;
  customTenantMakeModelData: CustomTenantMakeModelData | Nullish;
}

export const vehicleModelParser = (props: VehicleModelParserProps): VehicleModelDescriptor => {
  const unknownModels = props.unknownModels || [];

  const makeLabel =
    props.vehicleModelData?.[0]?.default_label || // global make label
    props.customMakeLabel || // custom make label
    props.fallbackMakeLabel; // fallback make label

  const customTenantModelSlice =
    props.customTenantMakeModelData?.[props.vehicleType || '']?.makes?.[props.customMakeLabel || '']
      ?.models;

  const customModels = customTenantModelSlice
    ? Object.keys(customTenantModelSlice).map(
        (key) =>
          ({
            label: key,
            value: key,
          }) as Option
      )
    : [];

  const globalModelsRaw = props.vehicleModelData?.[0]?.models || [];
  const globalModels = globalModelsRaw.map(
    (item) =>
      ({
        label: item.default_label,
        value: item.model,
      }) as Option
  );

  const modelsGroupedRaw = groupBy(
    (model) => model.model_group?.group || DEFAULT_MODEL_GROUP,
    globalModelsRaw
  );

  const groupedAllModelOptions: GroupType[] = Object.entries(modelsGroupedRaw).map(
    ([key, value]) => {
      const label = value?.[0]?.model_group?.default_label || makeLabel; // group label

      const optionsRaw =
        value?.map<Option>((item) => ({label: item.default_label, value: item.model})) || [];

      const defaultGroupOptions = [...optionsRaw, ...customModels, ...unknownModels];

      return {
        label,
        options: key === DEFAULT_MODEL_GROUP ? defaultGroupOptions : optionsRaw,
      };
    }
  );

  const flatModels = [...globalModels, ...customModels, ...unknownModels];

  const getGroupedModelOptions = () => {
    if (groupedAllModelOptions.length) {
      return groupedAllModelOptions;
    }

    if (customModels.length || unknownModels.length) {
      return [
        {
          label: makeLabel,
          options: [...customModels, ...unknownModels],
        },
      ];
    }

    return [];
  };

  return {
    groupedModelOptions: getGroupedModelOptions(),
    detail: {
      globalModels,
      customModels,
      flatModels,
    },
  };
};
